import React, { useEffect, useState } from 'react';
import {
    Paper, Switch, Button, TextField, Typography, Box, RadioGroup, FormControlLabel, Radio
} from '@mui/material';
import { Save, Campaign } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../../../api/axios';
import { SystemMessageColor, SystemMessageData } from '../../../../store/slices/systemMessageSlice';
import SystemMessageModal from "../modals/SystemMessageModal";

const SystemMessage: React.FC = () => {
    const [messageText, setMessageText] = useState('');
    const [color, setColor] = useState<SystemMessageColor>('BLUE');
    const [animated, setAnimated] = useState(false);
    const [enabled, setEnabled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Fetch initial settings from backend
    const fetchMessageSettings = async () => {
        try {
            const res = await api.get<SystemMessageData>('/system/message');
            setMessageText(res.data.messageText as string || '');
            setColor(res.data.color || 'BLUE');
            setAnimated(res.data.animated || false);
            setEnabled(res.data.enabled || false);
        } catch (err) {
            toast.error('Failed to load system message settings');
        }
    };

    useEffect(() => {
        fetchMessageSettings();
    }, []);

    // Handle saving data with MFA confirmation
    const handleConfirmSave = async (mfaCode?: string) => {
        setLoading(true);
        try {
            const payload = {
                messageText,
                color,
                animated,
                enabled,
            };

            await api.post('/system/message', payload, {
                headers: mfaCode ? { 'x-mfa-code': mfaCode } : {},
            });

            toast.success('System message updated successfully');
            setIsModalOpen(false);
            fetchMessageSettings();
        } catch (err) {
            toast.error('Failed to save system message settings');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Paper className="p-6 bg-white dark:bg-neutral-900 border border-gray-100 dark:border-neutral-800 shadow-2xl rounded-2xl">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                <Box>
                    <div className="flex items-center gap-2 mb-1">
                        <Campaign className="text-blue-500" />
                        <Typography className="text-xl font-bold text-gray-800 dark:text-neutral-100">
                            Global System Message Banner
                        </Typography>
                    </div>
                    <p className="text-xs text-gray-500 font-medium tracking-tight">
                        Configure global notification message displayed across the application top navigation
                    </p>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={() => setIsModalOpen(true)}
                    disabled={loading}
                    className="bg-dark-primary hover:bg-opacity-90 h-10 px-8 rounded-xl font-bold shadow-lg shadow-blue-500/20"
                >
                    Save Changes
                </Button>
            </div>

            <div className="flex flex-col gap-6">
                {/* Toggle active state */}
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-neutral-800/50 rounded-xl border border-gray-100 dark:border-neutral-800">
                    <div>
                        <span className="font-bold text-sm text-gray-800 dark:text-neutral-200">Enable System Message</span>
                        <p className="text-xs text-gray-500">Toggle whether the message banner is active globally</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Switch
                            checked={enabled}
                            onChange={(e) => setEnabled(e.target.checked)}
                            color="primary"
                        />
                        <span className={`text-xs font-black uppercase tracking-wider ${enabled ? 'text-emerald-500' : 'text-red-500'}`}>
              {enabled ? 'Active' : 'Disabled'}
            </span>
                    </div>
                </div>

                {/* Message Input */}
                <TextField
                    label="Message Content"
                    multiline
                    rows={3}
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    placeholder="Type global announcements here..."
                    fullWidth
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            backgroundColor: 'rgba(0,0,0,0.02)',
                        },
                    }}
                />

                {/* Color selection */}
                <div className="flex flex-col gap-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Banner Color</label>
                    <RadioGroup
                        row
                        value={color}
                        onChange={(e) => setColor(e.target.value as SystemMessageColor)}
                        className="gap-4"
                    >
                        <FormControlLabel
                            value="BLUE"
                            control={<Radio color="primary" />}
                            label={<span className="text-sm font-semibold text-blue-600 dark:text-blue-400">Blue Theme</span>}
                            className="border border-blue-200 dark:border-blue-900/50 rounded-xl px-4 py-1 m-0 bg-blue-50/50 dark:bg-blue-950/20"
                        />
                        <FormControlLabel
                            value="RED"
                            control={<Radio color="error" />}
                            label={<span className="text-sm font-semibold text-red-600 dark:text-red-400">Red Theme</span>}
                            className="border border-red-200 dark:border-red-900/50 rounded-xl px-4 py-1 m-0 bg-red-50/50 dark:bg-red-950/20"
                        />
                    </RadioGroup>
                </div>

                {/* Text Movement / Animation Toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-neutral-800/50 rounded-xl border border-gray-100 dark:border-neutral-800">
                    <div>
                        <span className="font-bold text-sm text-gray-800 dark:text-neutral-200">Marquee Animation</span>
                        <p className="text-xs text-gray-500">Enable text animation movement (Always active on small screens)</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Switch
                            checked={animated}
                            onChange={(e) => setAnimated(e.target.checked)}
                            color="secondary"
                        />
                        <span className="text-xs font-black uppercase tracking-wider text-gray-500">
              {animated ? 'Animated' : 'Static'}
            </span>
                    </div>
                </div>
            </div>

            <SystemMessageModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onConfirm={handleConfirmSave}
                loading={loading}
            />
        </Paper>
    );
};

export default SystemMessage;